/**
 * Servicio de autenticación para onboarding Kawescar.
 */
import {
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  linkWithPopup,
  GoogleAuthProvider,
} from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../../../firebase';

const LOGIN_URL = 'https://kawescar.cl/login';

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

export async function signUpEmail({ email, password }) {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email.trim(), password);
    return { success: true, user: userCredential.user };
  } catch (err) {
    const code = err.code || '';
    const message = err.message || 'Error al crear la cuenta';
    return { success: false, error: message, code };
  }
}

export async function resetPassword(email) {
  const normalized = (email || '').trim().toLowerCase();
  if (!normalized || !normalized.includes('@')) {
    return { success: false, error: 'Ingresa un correo válido', code: 'invalid-email' };
  }
  try {
    auth.languageCode = 'es';
    await sendPasswordResetEmail(auth, normalized, { url: LOGIN_URL, handleCodeInApp: false });
    return { success: true };
  } catch (err) {
    const code = err.code || '';
    if (code === 'auth/invalid-email') return { success: true };
    if (code === 'auth/too-many-requests') {
      return { success: false, error: 'Demasiados intentos. Prueba más tarde.', code };
    }
    return { success: true };
  }
}

export async function ensureUserDoc(uid, data) {
  const ref = doc(db, 'usuarios', uid);
  await setDoc(ref, { ...data, lastLoginAt: serverTimestamp() }, { merge: true });
}

/**
 * Inicia sesión con Google (popup). Si falla por popup bloqueado, usa redirect.
 * @returns {{ success: boolean, user?: import('firebase/auth').User, error?: string, code?: string }}
 */
export async function signInWithGooglePopup() {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    return { success: true, user: result.user };
  } catch (err) {
    const code = err?.code || '';
    const message = err?.message || 'Error al iniciar sesión con Google';
    return { success: false, error: message, code };
  }
}

/**
 * Inicia sesión con Google vía redirect (fallback cuando popup está bloqueado).
 */
export async function signInWithGoogleRedirect() {
  try {
    await signInWithRedirect(auth, googleProvider);
    return { success: true };
  } catch (err) {
    const code = err?.code || '';
    const message = err?.message || 'Error al redirigir a Google';
    return { success: false, error: message, code };
  }
}

/**
 * Obtiene el resultado de signInWithRedirect (llamar tras volver de Google).
 * @returns {{ success: boolean, user?: import('firebase/auth').User, error?: string, code?: string }}
 */
export async function handleGoogleRedirectResult() {
  try {
    const result = await getRedirectResult(auth);
    if (!result) return { success: false, error: null, code: null };
    return { success: true, user: result.user };
  } catch (err) {
    const code = err?.code || '';
    const message = err?.message || 'Error al completar el inicio de sesión';
    return { success: false, error: message, code };
  }
}

/**
 * Vincula la cuenta de Google a un usuario ya autenticado con email/contraseña.
 * Útil en perfil para "Vincular cuenta de Google".
 * @returns {{ success: boolean, user?: import('firebase/auth').User, error?: string, code?: string }}
 */
export async function linkGoogleToExistingAccount() {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    return { success: false, error: 'Debes iniciar sesión primero', code: 'auth/not-logged-in' };
  }
  try {
    const result = await linkWithPopup(currentUser, googleProvider);
    return { success: true, user: result.user };
  } catch (err) {
    const code = err?.code || '';
    const message = err?.message || 'Error al vincular cuenta de Google';
    return { success: false, error: message, code };
  }
}
